import { useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from '@mui/material'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import RefreshIcon from '@mui/icons-material/Refresh'
import {
  applyPrescriptionAgeDiagnostics,
  getPrescriptionAgeDiagnostics,
} from 'src/supportPortal/services/patientDiagnosticsService'

// All dates are epoch millis (UTC). Age math is done in UTC to match the backend (ZoneOffset.UTC).
function toDateInputValue(millis) {
  if (millis === null || millis === undefined) return ''
  return new Date(millis).toISOString().slice(0, 10) // yyyy-MM-dd (UTC)
}

function dateInputToMillis(dateStr) {
  if (!dateStr) return null
  const ms = Date.parse(`${dateStr}T00:00:00.000Z`)
  return Number.isNaN(ms) ? null : ms
}

function fmtDate(millis) {
  if (millis === null || millis === undefined) return '—'
  return new Date(millis).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  })
}

function computeAgeYears(dobMillis, asOnMillis) {
  if (dobMillis === null || dobMillis === undefined || asOnMillis === null || asOnMillis === undefined) {
    return null
  }
  const b = new Date(dobMillis)
  const o = new Date(asOnMillis)
  let age = o.getUTCFullYear() - b.getUTCFullYear()
  const m = o.getUTCMonth() - b.getUTCMonth()
  if (m < 0 || (m === 0 && o.getUTCDate() < b.getUTCDate())) age--
  return age < 0 ? null : age
}

const STEPS = ['Select prescriptions', 'Set "age as on" dates', 'Confirm & apply']

export default function PatientAgeDiagnostics({ patientNumber, patientName }) {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [data, setData] = useState(null)

  const [activeStep, setActiveStep] = useState(0)
  const [selectedIds, setSelectedIds] = useState(() => new Set())
  const [asOnById, setAsOnById] = useState({}) // prescriptionId -> yyyy-MM-dd

  const [dryRunLoading, setDryRunLoading] = useState(false)
  const [dryRunResult, setDryRunResult] = useState(null)
  const [applying, setApplying] = useState(false)
  const [applyResult, setApplyResult] = useState(null)
  const [applyError, setApplyError] = useState(null)

  const load = async () => {
    setLoading(true)
    setError(null)
    setDryRunResult(null)
    setApplyResult(null)
    setApplyError(null)
    try {
      const preview = await getPrescriptionAgeDiagnostics(patientNumber)
      setData(preview)
      const rows = preview?.prescriptions || []
      // Default the "age as on" date per row to its visit date.
      const initialDates = {}
      rows.forEach((r) => {
        initialDates[r.prescriptionId] = toDateInputValue(r.defaultAsOnEpochMillis)
      })
      setAsOnById(initialDates)
      // Pre-select the rows that would actually change (non-dummy, new age differs from what shows now).
      const preselect = new Set(
        rows
          .filter(
            (r) =>
              !r.isDummy &&
              r.previewNewAgeYears !== null &&
              r.previewNewAgeYears !== undefined &&
              r.previewNewAgeYears !== r.currentDisplayedAgeYears,
          )
          .map((r) => r.prescriptionId),
      )
      setSelectedIds(preselect)
      setActiveStep(0)
    } catch (e) {
      setError(e?.message || 'Failed to load diagnostics')
      setData(null)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (patientNumber) load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientNumber])

  const rows = useMemo(() => data?.prescriptions || [], [data])
  const dobMillis = data?.patientDateOfBirthEpochMillis
  const rowsById = useMemo(() => {
    const m = {}
    rows.forEach((r) => {
      m[r.prescriptionId] = r
    })
    return m
  }, [rows])

  const selectedRows = useMemo(
    () => rows.filter((r) => selectedIds.has(r.prescriptionId)),
    [rows, selectedIds],
  )

  const toggle = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const toggleAll = () => {
    setSelectedIds((prev) => {
      if (prev.size === rows.length) return new Set()
      return new Set(rows.map((r) => r.prescriptionId))
    })
  }

  const setAsOn = (id, value) => {
    setAsOnById((prev) => ({ ...prev, [id]: value }))
  }

  const goToConfirm = async () => {
    setDryRunLoading(true)
    setApplyError(null)
    setApplyResult(null)
    try {
      const result = await applyPrescriptionAgeDiagnostics({
        patientNumber,
        dryRun: true,
        entries: selectedRows.map((r) => ({
          prescriptionId: r.prescriptionId,
          ageAsOnEpochMillis: dateInputToMillis(asOnById[r.prescriptionId]),
        })),
      })
      setDryRunResult(result)
      setActiveStep(2)
    } catch (e) {
      setApplyError(e?.message || 'Failed to preview changes')
    } finally {
      setDryRunLoading(false)
    }
  }

  const doApply = async () => {
    setApplying(true)
    setApplyError(null)
    try {
      const result = await applyPrescriptionAgeDiagnostics({
        patientNumber,
        dryRun: false,
        entries: selectedRows.map((r) => ({
          prescriptionId: r.prescriptionId,
          ageAsOnEpochMillis: dateInputToMillis(asOnById[r.prescriptionId]),
        })),
      })
      setApplyResult(result)
    } catch (e) {
      setApplyError(e?.message || 'Failed to apply changes')
    } finally {
      setApplying(false)
    }
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
        <CircularProgress size={18} />
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          Loading prescriptions…
        </Typography>
      </Box>
    )
  }

  if (error) {
    return (
      <Box sx={{ py: 1 }}>
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" startIcon={<RefreshIcon />} onClick={load}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      </Box>
    )
  }

  const dob = fmtDate(dobMillis)

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', gap: 1, mb: 0.5 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          Fix prescription ages
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          Freezes the age each prescription shows to the patient&apos;s age on that visit, from the
          current date of birth.
        </Typography>
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 2,
          p: 1,
          mb: 1.5,
          borderRadius: 1,
          bgcolor: 'action.hover',
        }}
      >
        <Typography variant="caption">
          <b>Patient:</b> {patientName || data?.patientName || patientNumber}
        </Typography>
        <Typography variant="caption">
          <b>DOB:</b> {dob}
        </Typography>
        <Typography variant="caption">
          <b>Current age:</b>{' '}
          {data?.patientCurrentAgeYears !== null && data?.patientCurrentAgeYears !== undefined
            ? `${data.patientCurrentAgeYears} yrs`
            : '—'}
        </Typography>
      </Box>

      {rows.length === 0 ? (
        <Alert severity="info">This patient has no prescriptions.</Alert>
      ) : (
        <>
          <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 2 }}>
            {STEPS.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {/* STEP 0 — select */}
          {activeStep === 0 && (
            <Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  px: 1,
                  py: 0.5,
                  borderBottom: 1,
                  borderColor: 'divider',
                }}
              >
                <Checkbox
                  size="small"
                  checked={rows.length > 0 && selectedIds.size === rows.length}
                  indeterminate={selectedIds.size > 0 && selectedIds.size < rows.length}
                  onChange={toggleAll}
                />
                <Typography variant="caption" sx={{ color: 'text.secondary', flex: 1 }}>
                  Visit / doctor
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary', width: 150, textAlign: 'right' }}>
                  Shows now → will become
                </Typography>
              </Box>
              {rows.map((r) => {
                const willBe = r.previewNewAgeYears
                const changed = willBe !== null && willBe !== undefined && willBe !== r.currentDisplayedAgeYears
                return (
                  <Box
                    key={r.prescriptionId}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      px: 1,
                      py: 0.75,
                      borderBottom: 1,
                      borderColor: 'divider',
                    }}
                  >
                    <Checkbox
                      size="small"
                      checked={selectedIds.has(r.prescriptionId)}
                      onChange={() => toggle(r.prescriptionId)}
                    />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {fmtDate(r.visitDateEpochMillis)}
                        {r.isDummy && (
                          <Chip label="dummy" size="small" sx={{ ml: 0.75, height: 16, fontSize: 10 }} />
                        )}
                        {r.currentFrozenAgeYears !== null && r.currentFrozenAgeYears !== undefined && (
                          <Chip
                            label="already frozen"
                            size="small"
                            color="info"
                            variant="outlined"
                            sx={{ ml: 0.75, height: 16, fontSize: 10 }}
                          />
                        )}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }} noWrap>
                        {r.mentorName || 'Unknown doctor'}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        width: 150,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        gap: 0.5,
                      }}
                    >
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        {r.currentDisplayedAgeYears ?? '—'}
                      </Typography>
                      <ArrowForwardIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 700, color: changed ? 'success.main' : 'text.primary' }}
                      >
                        {willBe ?? '—'}
                      </Typography>
                    </Box>
                  </Box>
                )
              })}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                <Button size="small" startIcon={<RefreshIcon />} onClick={load}>
                  Reload
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  disabled={selectedIds.size === 0}
                  onClick={() => setActiveStep(1)}
                >
                  Next ({selectedIds.size})
                </Button>
              </Box>
            </Box>
          )}

          {/* STEP 1 — set dates */}
          {activeStep === 1 && (
            <Box>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                For each prescription, the age is computed as on this date (defaults to the visit
                date). Adjust if needed — the preview updates live.
              </Typography>
              <Box sx={{ mt: 1 }}>
                {selectedRows.map((r) => {
                  const asOn = asOnById[r.prescriptionId]
                  const newAge = computeAgeYears(dobMillis, dateInputToMillis(asOn))
                  return (
                    <Box
                      key={r.prescriptionId}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        flexWrap: 'wrap',
                        py: 0.75,
                        borderBottom: 1,
                        borderColor: 'divider',
                      }}
                    >
                      <Box sx={{ flex: 1, minWidth: 140 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Visit: {fmtDate(r.visitDateEpochMillis)}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                          {r.mentorName || 'Unknown doctor'}
                        </Typography>
                      </Box>
                      <TextField
                        type="date"
                        size="small"
                        label="Age as on"
                        value={asOn || ''}
                        onChange={(e) => setAsOn(r.prescriptionId, e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        sx={{ width: 170 }}
                      />
                      <Box sx={{ width: 90, textAlign: 'right' }}>
                        {newAge === null ? (
                          <Typography variant="caption" color="error">
                            invalid
                          </Typography>
                        ) : (
                          <Typography variant="body2" sx={{ fontWeight: 700, color: 'success.main' }}>
                            {newAge} yrs
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  )
                })}
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                <Button size="small" onClick={() => setActiveStep(0)}>
                  Back
                </Button>
                <Button variant="contained" size="small" onClick={goToConfirm} disabled={dryRunLoading}>
                  {dryRunLoading ? 'Checking…' : 'Review'}
                </Button>
              </Box>
            </Box>
          )}

          {/* STEP 2 — confirm & apply */}
          {activeStep === 2 && (
            <Box>
              {applyResult ? (
                <Alert severity="success" sx={{ mb: 1 }}>
                  Updated {applyResult.updated} prescription(s)
                  {applyResult.skipped ? `, skipped ${applyResult.skipped}` : ''}. Reload a
                  prescription to see the new age.
                </Alert>
              ) : (
                <Alert severity="warning" sx={{ mb: 1 }}>
                  This freezes the age on {dryRunResult?.results?.length ?? selectedRows.length}{' '}
                  prescription(s). The frozen age won&apos;t change even if the patient&apos;s DOB is
                  edited later.
                </Alert>
              )}

              {applyError && (
                <Alert severity="error" sx={{ mb: 1 }}>
                  {applyError}
                </Alert>
              )}

              <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}>
                <Box
                  sx={{
                    display: 'flex',
                    px: 1,
                    py: 0.5,
                    bgcolor: 'action.hover',
                    borderTopLeftRadius: 4,
                    borderTopRightRadius: 4,
                  }}
                >
                  <Typography variant="caption" sx={{ flex: 1, color: 'text.secondary' }}>
                    Prescription
                  </Typography>
                  <Typography variant="caption" sx={{ width: 110, color: 'text.secondary' }}>
                    Age as on
                  </Typography>
                  <Typography variant="caption" sx={{ width: 90, textAlign: 'right', color: 'text.secondary' }}>
                    New age
                  </Typography>
                </Box>
                {(applyResult?.results || dryRunResult?.results || []).map((res) => {
                  const row = rowsById[res.prescriptionId]
                  const skipped = (res.status || '').startsWith('skipped')
                  return (
                    <Box
                      key={res.prescriptionId}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        px: 1,
                        py: 0.5,
                        borderTop: 1,
                        borderColor: 'divider',
                      }}
                    >
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" noWrap>
                          {fmtDate(row?.visitDateEpochMillis)}
                        </Typography>
                        {skipped && (
                          <Typography variant="caption" color="error">
                            {res.status}
                          </Typography>
                        )}
                      </Box>
                      <Typography variant="caption" sx={{ width: 110 }}>
                        {fmtDate(res.ageAsOnEpochMillis)}
                      </Typography>
                      <Box sx={{ width: 90, textAlign: 'right' }}>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          {res.newAgeYears ?? '—'}
                          {res.oldFrozenAgeYears !== null && res.oldFrozenAgeYears !== undefined && (
                            <Typography component="span" variant="caption" sx={{ color: 'text.disabled', ml: 0.5 }}>
                              (was {res.oldFrozenAgeYears})
                            </Typography>
                          )}
                        </Typography>
                      </Box>
                    </Box>
                  )
                })}
              </Box>

              <Divider sx={{ my: 1.5 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                {applyResult ? (
                  <>
                    <span />
                    <Button variant="contained" size="small" onClick={load}>
                      Done
                    </Button>
                  </>
                ) : (
                  <>
                    <Button size="small" onClick={() => setActiveStep(1)} disabled={applying}>
                      Back
                    </Button>
                    <Button variant="contained" color="warning" size="small" onClick={doApply} disabled={applying}>
                      {applying ? 'Applying…' : 'Confirm & apply'}
                    </Button>
                  </>
                )}
              </Box>
            </Box>
          )}
        </>
      )}
    </Box>
  )
}
